<script>
import DiscountCodeComponent from './DiscountCodeComponent.vue';
import { inject, computed, ref } from 'vue';
import Flicking from '@egjs/vue3-flicking';
import { Pagination } from '@egjs/flicking-plugins';
import { Arrow } from '@egjs/flicking-plugins';
import '@egjs/vue3-flicking/dist/flicking.css';
import { checkPoints } from '../strings/ServiceTableCheckPoints';
import { useAppDataStore } from '../stores/appDataStore';

export default {
  components: {
    DiscountCodeComponent,
    Flicking: Flicking,
  },
  props: {
    appDataS3Value: {
      type: String,
      required: true
    },
    tooltipContent: {
      type: Object,
      required: true
    },
    tooltipContentWl: {
      type: Object,
      required: true
    },
    preCalculatedAppPackageData: {
      type: Object,
      required: true
    },
    appThemeBrand: {
      type: String,
      default: 'default'
    }
  },
  setup(props, { emit }) {
    const globalDiscountAvailable = inject('discountAvailable');
    const flicking = ref(null);
    let currentSlideIndex = ref(0);
    const appDataStore = useAppDataStore();
    const appData = computed(() => appDataStore.appData);

    const goToSlide = (index) => {
      if (flicking.value && !flicking.value.animating) {
        flicking.value.moveTo(index);
      }
    }

    const updateCurrentSlideIndex = () => {
      currentSlideIndex.value = flicking.value.currentPanel.index;
    }

    const plugins = [
      new Pagination({
        type: 'bullet'
      }),
      new Arrow()
    ]

    // Replaces the dot . with a comma , in prices
    const replaceDot = (value) => {
      return value.replace('.', ',');
    }

    // Checks if only the Basis package is available in step 5. Do not apply discount to this package
    const onlyBasisPackageAvailable = () => {
      return props.preCalculatedAppPackageData.basis && (!props.preCalculatedAppPackageData.komfort && !props.preCalculatedAppPackageData.premium);
    }

    const formatPlanName = (plan) => {
      return plan.replace(/_/g, ' '); // Replace underscores with spaces
    }

    const setSelectedPackage = (pack) => {
      let userSelectedPackage = props.preCalculatedAppPackageData[pack]
      let selectedPrice = userSelectedPackage.hasMonatlicherDiscount === true ? userSelectedPackage.discountedPrices.monatlicher : userSelectedPackage.priceFromApi
      appData.value.s5.userSelectedPrice = selectedPrice;
      appData.value.s5.value = pack;

      document.querySelector('.service-order-selection').scrollIntoView({ block: 'start',  behavior: 'smooth' });
    }

    return {
      replaceDot,
      onlyBasisPackageAvailable,
      formatPlanName,
      setSelectedPackage,
      goToSlide,
      updateCurrentSlideIndex,
      globalDiscountAvailable,
      checkPoints,
      plugins,
      flicking,
      currentSlideIndex
    }
  }
}
</script>

<template>
  <!-- Discount code component is only available for corporatebenefits whitelabel and applies only to Komfort & Premium packages -->
  <DiscountCodeComponent v-if="!onlyBasisPackageAvailable() && appThemeBrand === 'corporatebenefits'"></DiscountCodeComponent>
  <div id="tablet-service-table">
    <!-- Buttons to Control Slides -->
    <div class="plan-switcher">
      <div
        v-for="(planData, planTitle, index) in preCalculatedAppPackageData"
        :key="planTitle"
        class="plan"
        :class="{active: index === currentSlideIndex}"
        @click="goToSlide(index)"
      >
        {{ formatPlanName(planTitle) }}
      </div>
    </div>

    <Flicking ref="flicking" :options="{ circular: true }" :plugins="plugins" class="flicking-vertical" @changed="updateCurrentSlideIndex">
      <div v-for="(planData, planTitle) in preCalculatedAppPackageData" :key="planTitle" class="service-slide card-panel">
        <i v-if="planTitle == 'komfort'" class="fa-regular fa-thumbs-up"></i>
        <div class="service-slide__left">
          <div class="service-title">
            <h2>{{ formatPlanName(planTitle) }}</h2>
          </div>

          <div class="service-price">
            <div v-if="!planData.hasMonatlicherDiscount" class="price">
              <span class="service-price__number"> {{ replaceDot(planData.priceFromApi.toFixed(2)) }}€ </span>
            </div>
            <div v-else class="price discounted">
              <span class="service-price__number discounted"> {{ replaceDot(planData.discountedPrices.monatlicher.toFixed(2)) }}€ </span>
              <s>
                {{ replaceDot(planData.priceFromApi.toFixed(2)) }}€
              </s>
            </div>
            <span class="service-price__text">Monatlich*</span>
          </div>
          <div class="service-left__bottom">
            <div class="service-price-einmaliges">
              <div v-if="!planData.hasEinmaligesDiscount" class="price">
                <span class="service-price__number" style="display: block"> <strong> {{ replaceDot(planData.einmaligesPrice.toFixed(2)) }}€ </strong>
                  / Einmaliges  <br/>Einrichtungsentgelt *
                </span>
              </div>

              <div v-else class="price">
                <s>
                  <span class="service-price__number" style="display: block"> {{ replaceDot(planData.einmaligesPrice.toFixed(2)) }}€ </span>
                </s>
                <span class="discounted">
                  <strong> {{ replaceDot(planData.discountedPrices.einmaliges.toFixed(2)) }}€ </strong>
                </span>
                <span> / Einmaliges Einrichtungsentgelt*</span>
              </div>
            </div>

            <div class="service-angebot-button">
              <button class="button button--angebot -no-margin" @click="setSelectedPackage(planTitle)">
                <i class="fa-solid fa-plus"></i>
                <span>Angebot auswählen</span>
              </button>
            </div>
          </div>
        </div>

        <div class="service-slide__right">
          <ul class="service-bulletin__list">
            <li
              v-for="point in checkPoints"
              :key="point.title"
              class="tablet-row"
              :class="{disabled: !point.packages.includes(planTitle)}"
            >
              <i class="icon" :class="point.packages.includes(planTitle) ? 'icon--check' : 'icon--not-checked'" />
              <span class="tablet-row-content__text" v-html="point.title"></span>
              <div v-tooltip="{html: true, content: appThemeBrand === 'default' ? tooltipContent[point.tooltipContent] : tooltipContentWl[point.tooltipContent]}"
                   class="icon fa-circle-question" />
            </li>
          </ul>
        </div>
      </div>

      <template #viewport>
        <div class="slider-controls">
          <span class="flicking-arrow-prev is-circle">
            <i class="icon icon--left-arrow"></i>
          </span>
          <div class="flicking-pagination"></div>
          <span class="flicking-arrow-next is-circle">
            <i class="icon icon--right-arrow"></i>
          </span>
        </div>
      </template>
    </Flicking>
  </div>
</template>

<style>
@import '~brandVariables/variables.scss';

.fa-regular.fa-thumbs-up {
  background: #ff1f58;
  border-radius: 50%;
  padding: 10px;
  position: absolute;
  left: 30px;
  top: 0;

  &:before {
    font-size: 30px;
    color: #FFFFFF;
  }
}

.inner-row-ul {
  margin-left: 25px;
  list-style: disc;
}

#tablet-service-table {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  .service-slide {
    position: relative;
    display: flex;
    min-height: 650px;
    width: 93%;
    padding: 15px;
    background: #FFFFFF;
    border-radius: 10px;
    gap: 20px;

    .service-slide__left {
      display: flex;
      flex-direction: column;
      background: $main-color;
      align-items: center;
      width: 45%;
      border-radius: 10px;

      .service-title {
        width: 100%;
        height: 12%;
        display: flex;
        justify-content: center;
        align-items: center;
        text-transform: capitalize;

        h2 {
          color: #FFFFFF;
          font-size: 20px
        }
      }

      .service-price {
        background: #deeefc;
        width: 100%;
        height: 18%;
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;

        .service-price__number {
          font-size: 36px;
          font-weight: 900;
          color: $main-color;
          &.discounted {
            color: #eb003c;
          }
        }

        s {
          font-size: 16px;
          font-weight: 700;
          color: #777;
          padding: 10px;
        }

        span.service-price__text {
          font-size: 16px;
        }
      }

      .service-left__bottom {
        position: relative;
        display: flex;
        flex-direction: column;
        align-items: center;
        width: 100%;
        height: 70%;

        .service-price-einmaliges {
          color: #FFFFFF;
          display: flex;
          flex-direction: column;
          font-size: 18px;
          text-align: center;
          position: absolute;
          top: 1.56rem;
        }
        .service-angebot-button {
          position: absolute;
          bottom: 1.56rem;
          width: 100%;
          display: flex;
          justify-content: center;

          .button--angebot {
            padding: 1rem;
            display: flex;
            width: 85%;
            justify-content: space-evenly;
            gap: 15px;
            align-items: baseline;

            .fa-solid {
              padding: 5px 6.5px;
            }

            &:after {
              content: '';
            }
          }
        }
      }
    }
    .service-slide__right {
      width: 55%;

      .service-bulletin__list {
        height: 100%;
        width: 100%;
        list-style: none;

        .tablet-row {
          height: 12.5%;
          width: 95%;
          position: relative;
          font-size: 14px;
          line-height: 16px;
          color: #000548;
          display: flex;
          margin: 0 10px;
          padding: 12px 0;
          justify-content: left;
          align-items: center;

          &:not(:first-child) {
            border-top: 1px solid rgba(119, 119, 119, .24);
          }

          &.disabled {
            opacity: 0.5;
          }

          .tablet-row-content__text {
            width: 70%;
            position: relative;
            left: 2.2rem;
          }

          .icon {
            &.icon--check {
              &:after {
                font-size: 2.2rem;
              }
            }
            &.icon--not-checked {
              &:after {
                font-size: 2.2rem;
              }
            }
          }

          .icon.fa-circle-question {
            position: absolute;
            right: 0;
            &:before {
              content: "\f059";
              font-size: 26px;
            }
            &:after {
              content: '';
            }
          }
        }
      }
    }
  }

  .flicking-pagination {
    gap: 5px;
    top: 25%;

    .flicking-pagination-bullet {
      height: 16px;
      width: 16px;
    }
  }

  .slider-controls {
    .flicking-arrow-next,
    .flicking-arrow-prev {
      height: 64px;
      width: 64px;
      &.is-circle {

        .icon {
          color: white;
          top: 35%;
          left: 30%;

          &::after {
            font-size: 2.5rem;
          }
        }
      }
    }
  }
}
</style>
